import {
  Autocomplete,
  Button,
  Checkbox,
  Indicator,
  Menu,
  Skeleton,
  clsx,
} from '@mantine/core';
import classNames from 'classnames';
import Head from 'next/head';
import {useRouter} from 'next/router';
import React, {type FC} from 'react';
import {MdFilterAlt, MdSearch, MdUnfoldMore} from 'react-icons/md';
import {TITLE_SUFFIX} from '../../../config/constants';
import {COLOR_BACKGROUND_1} from '../../../config/constants/colorCodes';
import {GoogleAdBannerName} from '../../../config/constants/googleAdBanner';
import {GTAG_LOCATION} from '../../../config/constants/gtag';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {HOME, REPORTS} from '../../../config/constants/routes';
import {Crumb} from '../../../core/entities/reports/constants';
import {useReportsCount} from '../../../core/repositories/reportsRepository';
import {useAuth} from '../../../core/usecases/auth/AuthContext';
import {GoogleAdBanner} from '../../../shared/components/Banner/GoogleAdBanner';
import {SpHeaderBanner} from '../../../shared/components/Banner/SpHeaderBanner';
import {CommonLink} from '../../../shared/components/CommonLink';
import {useGoogleAdBanner} from '../../../shared/services/useGoogleAdBanner';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {
  type UseHandleReportsByDepartmentReturnType,
  useHandleReportsByDepartment,
} from '../usecase/useHandleReportsByDepartment';
import {ReportCountCard} from './Container/Presenter/ReportCountCard';
import {FloatBanner} from './FloatBanner';
import {ReportsHeader} from './Header';

const CompanyNameSearchInput: FC<{
  searchWord: string;
  autoCompleteData: Props['handleReportParams']['autoCompleteData'];
  isPC: boolean;
  handleSearchInputChange: (searchWord: string) => void;
}> = ({searchWord, autoCompleteData, isPC, handleSearchInputChange}) => {
  return (
    <Autocomplete
      placeholder={'企業名で検索'}
      icon={<MdSearch size="28px" />}
      itemComponent={(props) => (
        <CommonLink
          href={`/company/${props.id}/experiences`}
          className="border-b-2 cursor-pointer flex items-center line-clamp-2 hover:underline text-primary p-4"
        >
          {props.value}
        </CommonLink>
      )}
      data={autoCompleteData}
      value={searchWord}
      onChange={(searchWord) => {
        handleSearchInputChange(searchWord);
      }}
      size="sm"
      dropdownPosition="bottom"
      limit={1000}
      maxDropdownHeight={320}
      styles={{
        input: {
          backgroundColor: COLOR_BACKGROUND_1,
          border: 'none',
          borderRadius: '20px',
          '::placeholder': {
            color: 'black',
          },
          height: isPC ? 40 : 36,
        },
        icon: {
          color: 'black',
        },
      }}
    />
  );
};

type Props = {
  pageTitle: string;
  description: string;
  departmentId: number;
  departmentName: string;
  handleReportParams: UseHandleReportsByDepartmentReturnType;
  handleChangeReportType: (value: string[]) => void;
};

const ReportByDepartmentContent: React.FC<Props> = ({
  pageTitle,
  description,
  departmentId,
  departmentName,
  handleReportParams: {
    industryCategories,
    filterItems,
    reportsCount,
    searchWord,
    autoCompleteData,
    handleSearchInputChange,
    reportTypes,
  },
  handleChangeReportType,
}) => {
  // DFPバナーのセット
  const googleAdBannerNames = [GoogleAdBannerName.RICH_DESKTOP_HEADER];
  useGoogleAdBanner(googleAdBannerNames);

  const {isPC} = useMediaQuery();
  const {status} = useAuth();

  const IndustrySelector = () => {
    return (
      <div className="h-68 bg-white flex justify-around gap-8 overflow-x-scroll pc:w-[1200px] pc:mt-16 pc:rounded-[20px] pc:shadow-card pc:px-16 pc:py-8">
        {industryCategories?.ok &&
          industryCategories.value.industryCategories.map((industry) => (
            <CommonLink
              key={industry.id}
              className={clsx(
                'text-base text-primary font-bold border-2 border-primary rounded-[20px] px-8 py-4 pc:px-16 pc:py-8 text-nowrap hover:cursor-pointer',
              )}
              href={`/reports/industry/${industry.id}?department=${departmentId}`}
            >
              {industry.name}
            </CommonLink>
          ))}
        {!industryCategories?.ok && <Skeleton height={40} />}
      </div>
    );
  };

  const crumbs = [
    {label: Crumb.HOME, href: HOME},
    {label: Crumb.REPORTS, href: REPORTS},
    {label: departmentName, href: `/reports/department/${departmentId}`},
  ];

  return (
    <>
      <div className="h-[152px] bg-black-5 hidden pc:block">
        <GoogleAdBanner
          googleAdBannerName={GoogleAdBannerName.RICH_DESKTOP_HEADER}
        />
      </div>
      {status === 'authenticated' && (
        <SpHeaderBanner
          location={GTAG_LOCATION.REPORTS_BY_DEPARTMENT}
          className="p-20 pc:hidden"
        />
      )}

      <ReportsHeader
        pageTitle={pageTitle}
        crumbs={crumbs}
        description={description}
      />

      <div className="hidden pc:block h-68">
        <IndustrySelector />
      </div>

      <div className="w-full pc:w-[1200px]">
        <div
          className={classNames(
            'h-68 pc:h-[60px] px-16 py-12 bg-white rounded-[20px] shadow-card justify-between mx-16 pc:flex pc:gap-8 pc:mt-16 mt-8 pc:mx-0',
          )}
        >
          <div className="pc:hidden">
            <div className="h-40">
              <IndustrySelector />
            </div>
            <div className="h-36 mt-8">
              <CompanyNameSearchInput
                searchWord={searchWord}
                autoCompleteData={autoCompleteData}
                handleSearchInputChange={handleSearchInputChange}
                isPC={isPC}
              />
            </div>
          </div>

          <div className="h-36 flex my-4 pc:gap-12">
            <Menu>
              <Menu.Target>
                <Button
                  className={classNames('rounded-[20px] border-none', {
                    'bg-background text-primary': isPC,
                    'bg-white text-black px-[2px]': !isPC,
                  })}
                  variant="outline"
                >
                  <div
                    className={classNames('flex', {
                      'gap-8': isPC,
                    })}
                  >
                    {filterItems.reportType.length > 0 ? (
                      <Indicator size={6} color="red">
                        <MdFilterAlt size={20} />
                      </Indicator>
                    ) : (
                      <MdFilterAlt size={20} />
                    )}
                    <span
                      className={classNames({
                        'p-4': isPC,
                        'pt-4': !isPC,
                      })}
                    >
                      レポートの種類
                    </span>
                    <MdUnfoldMore size={20} />
                  </div>
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Checkbox.Group
                  value={filterItems.reportType.map(String)}
                  onChange={handleChangeReportType}
                >
                  {reportTypes.types.map((type) => (
                    <Checkbox
                      key={type.id}
                      className="p-4"
                      styles={{
                        labelWrapper: {
                          width: '100%',
                          userSelect: 'none',
                        },
                      }}
                      value={String(type.id)}
                      label={
                        <span className="w-full mx-auto">{type.name}</span>
                      }
                    />
                  ))}
                </Checkbox.Group>
              </Menu.Dropdown>
            </Menu>
          </div>

          <div className="hidden pc:block w-1/2">
            <CompanyNameSearchInput
              searchWord={searchWord}
              autoCompleteData={autoCompleteData}
              handleSearchInputChange={handleSearchInputChange}
              isPC={isPC}
            />
          </div>
          <div className="hidden pc:block" />
        </div>
      </div>
      <div
        className={classNames(
          'mt-16 grid grid-cols-1 mx-16 gap-8 pc:w-[1200px] pc:grid-cols-3 pc:gap-16',
        )}
      >
        {reportsCount?.ok &&
          reportsCount.value.map((category, i) => (
            <React.Fragment key={i}>
              {category.companyList.map((company) => (
                <ReportCountCard key={company.companyID} company={company} />
              ))}
            </React.Fragment>
          ))}
        {!reportsCount?.ok &&
          Array.from({length: 30}).map((_, i) => (
            <div key={i} className="pc:h-[110px] pc:inline p-16">
              <Skeleton height={30} className="p-4" />
              <div className="h-20" />

              <Skeleton height={30} className="p-4" />
            </div>
          ))}
      </div>

      <FloatBanner />
    </>
  );
};

type ReportByDepartmentPageProps = {
  departmentName?: string;
};

export const ReportByDepartmentPage: React.FC<ReportByDepartmentPageProps> = ({
  departmentName,
}) => {
  const router = useRouter();
  const {departmentId: departmentIdParam} = router.query as {
    departmentId: string;
  };

  const departmentId = Number.parseInt(departmentIdParam, 10);
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath.replace(/\?.*$/, ''))}`;

  const handleReportParams = useHandleReportsByDepartment({
    departmentId,
  });
  const {filterItems, setFilterItems} = handleReportParams;

  const pageTitle = departmentName
    ? `${departmentName}に関するインターン・本選考のES・選考体験記一覧`
    : 'インターン・本選考のES・選考体験記一覧';

  const {data: reportsCountByCompany} = useReportsCount({
    queryParams: {
      types: [],
      industryCategoryIDs: [],
      departmentMasterIDs: [departmentId],
    },
  });
  const reportCountSum = reportsCountByCompany?.ok
    ? reportsCountByCompany.value.reduce(
        (acc1, cur1) =>
          acc1 +
          cur1.companyList.reduce(
            (acc2, cur2) =>
              acc2 +
              cur2.counts.entrysheetCount +
              cur2.counts.internCount +
              cur2.counts.selectionCount,
            0,
          ),
        0,
      )
    : 0;

  const handleChangeReportType = (value: string[]) => {
    const newFilterItems = {
      ...filterItems,
      reportType: value.map(Number),
    };
    setFilterItems(newFilterItems);
  };

  const description = `${departmentName}職に関するES・体験記は合計${reportCountSum}件です。本選考、インターンシップ選考で内定もしくは最終面接まで進んだ方からいただいた非常に信頼度が高い情報をまとめております。`;

  return (
    <>
      <Head>
        <title key="title">{pageTitle + TITLE_SUFFIX}</title>
        <meta key="description" name="description" content={description} />
        <meta
          key="og:title"
          property="og:title"
          content={`${departmentName}に関するES・体験記`}
        />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta
          key="og:description"
          name="og:description"
          content={description}
        />
        <meta
          name="keywords"
          content="外資,就活,インターンシップ,新卒採用,会社説明会,コラム,ES,エントリーシート・体験記,体験談,投資銀行,コンサル,商社,エンジニア,SWE,内定,本選考"
        />
        <link rel="canonical" href={currentUrl} key="canonical" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1.0,maximum-scale=1.0"
        />
      </Head>

      <ReportByDepartmentContent
        pageTitle={pageTitle}
        description={description}
        departmentId={departmentId}
        departmentName={departmentName ?? ''}
        handleReportParams={handleReportParams}
        handleChangeReportType={handleChangeReportType}
      />
    </>
  );
};
